/** RFC 0345 3e, 3j: an agent key reaches a project or session through a
 * group of its issuer's, never by a direct key grant on the target.
 *
 * Keyhive keeps one capability per key and target: when Sam and an owner both
 * grant one agent key directly on a project, the two grants coalesce, and no
 * remover can take back Sam's without risking the owner's. So each person who
 * grants agents on a target does it through their own child group: the group
 * is a member of the target at one level, the person is its only admin, and
 * the agents are its other members. Removing Sam revokes Sam's groups; an
 * owner's group, holding the same key, keeps it in.
 *
 * Nothing records which groups are issuer groups: the shape is read from the
 * signed grants. Projects and sessions have no other child groups than people,
 * and a person's group holds devices only, so a direct child with exactly one
 * admin group member and nothing else but non-admin keys is an issuer group.
 */
import {
  Access,
  type Capability,
  GroupId,
  type Identifier,
  type Keyhive,
  MemberedId,
} from '@automerge/automerge-repo-keyhive'

const hex = (bytes: Uint8Array) =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')

export interface IssuerGroup {
  id: GroupId
  /** The group, hex. */
  group: string
  /** The issuing person, hex: the group's only admin. */
  person: string
  /** Its level on the target. */
  can: Access
  /** The device that signed the group's place on the target. */
  signer: string
  agents: Array<{ key: string; can: Access; signer: string; grant: Capability }>
}

/** The issuer shape of one group, or undefined if it is not one. */
export async function issuerShape(
  hive: Keyhive,
  id: GroupId,
): Promise<{ person: string; agents: IssuerGroup['agents'] } | undefined> {
  const members = (await (await hive.getGroup(id))?.members()) ?? []
  const admins = members.filter((m) => m.can.equals(Access.admin()))
  if (admins.length !== 1 || !admins[0].who.isGroup) return
  const rest = members.filter((m) => m !== admins[0])
  if (rest.some((m) => !m.who.isIndividual)) return
  return {
    person: hex(admins[0].who.id.toBytes()),
    agents: rest.map((m) => ({
      key: hex(m.who.id.toBytes()),
      can: m.can,
      signer: hex(m.proof.verifyingKey),
      grant: m,
    })),
  }
}

/** Every issuer group that is a direct member of the target group. */
export async function issuerGroups(
  hive: Keyhive,
  target: GroupId,
): Promise<IssuerGroup[]> {
  const found: IssuerGroup[] = []
  for (const grant of (await (await hive.getGroup(target))?.members()) ?? []) {
    if (!grant.who.isGroup || grant.can.equals(Access.admin())) continue
    const id = new GroupId(grant.who.id.toBytes())
    const shape = await issuerShape(hive, id)
    if (!shape) continue
    found.push({
      id,
      group: hex(id.toBytes()),
      can: grant.can,
      signer: hex(grant.proof.verifyingKey),
      ...shape,
    })
  }
  return found
}

/** The person's issuer group on a target at this level, made if missing. The
 * creating device leaves it, so the person, not one device, administers it. */
export async function issuerGroupOn(
  hive: Keyhive,
  person: GroupId,
  target: GroupId,
  level: Access,
): Promise<GroupId> {
  const personHex = hex(person.toBytes())
  const existing = (await issuerGroups(hive, target)).find(
    (group) => group.person === personHex && group.can.equals(level),
  )
  if (existing) return existing.id
  const group = await hive.generateGroup([person.toIdentifier()])
  await hive.revokeMember(hive.id.toIdentifier(), true, MemberedId.group(group))
  await hive.addMember(
    group.toIdentifier(),
    MemberedId.group(target),
    level,
    [],
  )
  return group
}

/** Grant an agent key on a project or session group, as this person. */
export async function grantAgentThroughIssuer(
  hive: Keyhive,
  person: GroupId,
  agent: Identifier,
  target: GroupId,
  level: Access,
): Promise<GroupId> {
  const group = await issuerGroupOn(hive, person, target, level)
  const key = hex(agent.toBytes())
  const shape = await issuerShape(hive, group)
  if (!shape?.agents.some((a) => a.key === key && a.can.atLeast(level)))
    await hive.addMember(agent, MemberedId.group(group), level, [])
  return group
}

/** The person's own grants of these keys on a target: their issuer groups,
 * and any older direct grant a device of theirs signed. Revokes them and
 * leaves every other person's grant of the same key alone. */
export async function revokeIssuedAgentGrants(
  hive: Keyhive,
  person: GroupId,
  target: GroupId,
  keys: readonly string[],
  devices: ReadonlySet<string>,
): Promise<{ revoked: number; left: string[] }> {
  const personHex = hex(person.toBytes())
  let revoked = 0
  const left: string[] = []
  const revoke = async (who: Identifier, where: GroupId, key: string) => {
    try {
      await hive.revokeMember(who, true, MemberedId.group(where))
      revoked++
    } catch {
      left.push(key)
    }
  }
  for (const group of await issuerGroups(hive, target)) {
    if (group.person !== personHex) continue
    for (const agent of group.agents)
      if (keys.includes(agent.key))
        await revoke(agent.grant.who.id, group.id, agent.key)
  }
  for (const grant of (await (await hive.getGroup(target))?.members()) ?? []) {
    const key = hex(grant.who.id.toBytes())
    if (
      grant.who.isIndividual &&
      keys.includes(key) &&
      devices.has(hex(grant.proof.verifyingKey))
    )
      await revoke(grant.who.id, target, key)
  }
  return { revoked, left }
}

/** Keys granted on a target, directly (older grants) or through an issuer
 * group, each with the device that signed its own grant. */
export async function agentKeysOn(
  hive: Keyhive,
  target: GroupId,
): Promise<Array<{ key: string; can: Access; signer: string }>> {
  const out: Array<{ key: string; can: Access; signer: string }> = []
  for (const grant of (await (await hive.getGroup(target))?.members()) ?? [])
    if (grant.who.isIndividual && grant.proof.verify())
      out.push({
        key: hex(grant.who.id.toBytes()),
        can: grant.can,
        signer: hex(grant.proof.verifyingKey),
      })
  for (const group of await issuerGroups(hive, target))
    for (const agent of group.agents)
      if (agent.grant.proof.verify())
        out.push({
          key: agent.key,
          can: agent.can.atLeast(group.can) ? group.can : agent.can,
          signer: agent.signer,
        })
  return out
}
